import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { CredentialRevealGuardProvider, useCredentialRevealGuard } from "./auth/CredentialRevealGuard";
import { CapabilityRoute, ProtectedRoute, SelfOrCapabilityRoute, GuestRoute } from "./auth/ProtectedRoute";
import headerLogo from "./assets/iedc-logo-navbar.png";
import { IdentityMenu } from "./components/IdentityMenu";
import { LoginPage } from "./pages/LoginPage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { MfaEnrollPage } from "./pages/MfaEnrollPage";
import { MfaChallengePage } from "./pages/MfaChallengePage";
import { MfaReplacementPage } from "./pages/MfaReplacementPage";
import { MfaRevocationPage } from "./pages/MfaRevocationPage";
import { MfaRecoveryRequestPage } from "./pages/MfaRecoveryRequestPage";
import { MfaRecoveryApprovalPage } from "./pages/MfaRecoveryApprovalPage";
import { MfaRecoveryEnrollmentPage } from "./pages/MfaRecoveryEnrollmentPage";
import { DashboardPage } from "./pages/DashboardPage";
import { MemberDetailPage } from "./pages/MemberDetailPage";
import { MembersPage } from "./pages/MembersPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { MemberCreatePage } from "./pages/MemberCreatePage";
import { MemberEditPage } from "./pages/MemberEditPage";
import { MemberDepartmentPage } from "./pages/MemberDepartmentPage";
import { SupportCredentialsPage } from "./pages/SupportCredentialsPage";
import { AccessManagementPage } from "./pages/AccessManagementPage";
import { ContributionPage } from "./pages/ContributionPage";
import { MemberFinancePage } from "./pages/MemberFinancePage";
import type { SessionCapabilities } from "./api/sessionContext";
import { type MouseEventHandler, useEffect, useRef, useState } from "react";
import { isOperationalUser, roleLabel } from "./auth/sessionRole";

export { isOperationalUser, roleLabel };

interface NavigationItem {
  readonly to: string;
  readonly label: string;
  readonly isVisible: (caps: SessionCapabilities | null | undefined, hasMember: boolean, isOperational: boolean) => boolean;
}

const NAVIGATION_ITEMS: readonly NavigationItem[] = [
  {
    to: "/contribuicoes",
    label: "Mensalidades",
    isVisible: (_caps, hasMember, isOperational) => hasMember && !isOperational,
  },
  {
    to: "/associados",
    label: "Associados",
    isVisible: (caps) => Boolean(caps?.read_members),
  },
  {
    to: "/associados/novo",
    label: "Novo associado",
    isVisible: (caps) => Boolean(caps?.create_members),
  },
  {
    to: "/acessos",
    label: "Gestão de acessos",
    isVisible: (caps) => Boolean(caps?.manage_access),
  },
  {
    to: "/mfa/recuperacao/solicitar",
    label: "Recuperação MFA",
    isVisible: (caps) => Boolean(caps?.request_mfa_recovery),
  },
  {
    to: "/mfa/recuperacao/aprovar",
    label: "Aprovações MFA",
    isVisible: (caps) => Boolean(caps?.approve_mfa_recovery),
  },
];

function HeaderNavigation({
  capabilities,
  hasMember,
  isReady,
  isOpen,
  onNavigate,
  onLogout,
}: {
  capabilities: SessionCapabilities | null | undefined;
  hasMember: boolean;
  isReady: boolean;
  isOpen: boolean;
  onNavigate: MouseEventHandler<HTMLAnchorElement>;
  onLogout: () => void;
}) {
  const isOperational = isOperationalUser(capabilities);

  return (
    <nav
      id="app-header-nav"
      className={`app-header__nav${isOpen ? " app-header__nav--open" : ""}`}
      aria-label="Navegação principal"
    >
      <NavLink to="/dashboard" onClick={onNavigate}>
        Início
      </NavLink>
      {isReady &&
        NAVIGATION_ITEMS.map((item) =>
          item.isVisible(capabilities, hasMember, isOperational) ? (
            <NavLink key={item.to} to={item.to} onClick={onNavigate}>
              {item.label}
            </NavLink>
          ) : null,
        )}
      {isOpen && (
        <button type="button" className="app-header__nav-logout" onClick={onLogout}>
          Sair
        </button>
      )}
    </nav>
  );
}

function AuthenticatedHeaderActions() {
  const { state, logout } = useAuth();
  const { canLeave, guardNavigation, notifyBlocked, blockedAttempt } = useCredentialRevealGuard();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const actionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    }

    function handleClickOutside(event: MouseEvent) {
      if (actionsRef.current && !actionsRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  function handleNavClick(event: React.MouseEvent<HTMLAnchorElement>) {
    setIsMenuOpen(false);
    guardNavigation(event);
  }

  function guardLogout() {
    if (!canLeave) {
      notifyBlocked();
      return;
    }
    logout();
  }

  if (state.status !== "authenticated" || state.requiresPasswordChange) return null;
  const isReady = state.sessionContextStatus === "ready";
  const capabilities = isReady ? state.sessionContext?.capabilities : null;
  const hasMember = Boolean(state.memberId);

  return (
    <div ref={actionsRef} className="app-header__actions">
      <HeaderNavigation
        capabilities={capabilities}
        hasMember={hasMember}
        isReady={isReady}
        isOpen={isMenuOpen}
        onNavigate={handleNavClick}
        onLogout={() => {
          setIsMenuOpen(false);
          guardLogout();
        }}
      />
      <div className="app-header__user-controls">
        <IdentityMenu
          username={state.username}
          roleLabel={roleLabel(capabilities, isReady && hasMember)}
          onLogout={guardLogout}
          onPasswordChangeClick={guardNavigation}
          onMfaClick={guardNavigation}
        />
        <button
          type="button"
          className="app-header__menu-toggle"
          aria-expanded={isMenuOpen}
          aria-controls="app-header-nav"
          aria-label={isMenuOpen ? "Fechar navegação" : "Abrir navegação"}
          onClick={() => setIsMenuOpen((current) => !current)}
        >
          {isMenuOpen ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="18" x2="20" y2="18" />
            </svg>
          )}
        </button>
      </div>
      {blockedAttempt && <p className="app-header__guard-notice" role="status">Confirme o recebimento da senha temporária antes de sair desta tela.</p>}
    </div>
  );
}

function AppHeader() {
  const { guardNavigation } = useCredentialRevealGuard();

  return (
    <header className="app-header">
      <NavLink to="/dashboard" className="app-header__brand" aria-label="IEDC Associados, ir ao início" onClick={guardNavigation}>
        <img className="app-header__logo" src={headerLogo} alt="" width="180" height="60" />
      </NavLink>
      <AuthenticatedHeaderActions />
    </header>
  );
}

function RootRedirect() {
  const { state } = useAuth();
  if (state.status !== "authenticated") return <Navigate to="/login" replace />;
  if (state.requiresPasswordChange) return <Navigate to="/trocar-senha" replace />;
  // ADR-054, "Correção e ajustes (25/08/2026)" item 2: sem essa checagem, / deixaria um trabalhador
  // pendente cair direto no dashboard, quebrando o bloqueio da aplicação inteira. Sem prop de escape —
  // ninguém acessa / com escape (só a própria rota /mfa/matricula, registrada abaixo).
  if (state.requiresMfaEnrollment) return <Navigate to="/mfa/matricula" replace />;
  return <Navigate to="/dashboard" replace />;
}

function TitularContributionRoute() {
  const { state } = useAuth();
  if (state.status !== "authenticated") return <Navigate to="/login" replace />;
  if (state.sessionContextStatus === "loading") return null;
  if (
    state.sessionContextStatus !== "ready" ||
    !state.memberId ||
    isOperationalUser(state.sessionContext?.capabilities)
  ) {
    return <Navigate to="/dashboard" replace />;
  }
  return <ContributionPage />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route
        path="/login"
        element={
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        }
      />
      <Route
        path="/trocar-senha"
        element={
          <ProtectedRoute allowPasswordChangeRequired>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mfa/matricula"
        element={
          <ProtectedRoute allowMfaEnrollmentRequired>
            <MfaEnrollPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mfa/desafio"
        element={
          <ProtectedRoute>
            <MfaChallengePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mfa/troca"
        element={
          <ProtectedRoute>
            <MfaReplacementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mfa/revogar"
        element={
          <ProtectedRoute>
            <MfaRevocationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mfa/recuperacao/solicitar"
        element={
          <ProtectedRoute>
            <CapabilityRoute capability="request_mfa_recovery"><MfaRecoveryRequestPage /></CapabilityRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mfa/recuperacao/aprovar"
        element={
          <ProtectedRoute>
            <CapabilityRoute capability="approve_mfa_recovery"><MfaRecoveryApprovalPage /></CapabilityRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/mfa/recuperacao/concluir"
        element={
          <ProtectedRoute allowMfaEnrollmentRequired>
            <MfaRecoveryEnrollmentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route path="/contribuicoes" element={<ProtectedRoute><TitularContributionRoute /></ProtectedRoute>} />
      <Route path="/associados/:memberId/financeiro" element={<ProtectedRoute><CapabilityRoute capability="read_payments"><MemberFinancePage /></CapabilityRoute></ProtectedRoute>} />
      <Route
        path="/associados"
        element={
          <ProtectedRoute>
            <MembersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/associados/:memberId"
        element={
          <ProtectedRoute>
            <MemberDetailPage />
          </ProtectedRoute>
        }
      />
      <Route path="/associados/novo" element={<ProtectedRoute><CapabilityRoute capability="create_members"><MemberCreatePage /></CapabilityRoute></ProtectedRoute>} />
      <Route path="/associados/:memberId/editar" element={<ProtectedRoute><SelfOrCapabilityRoute capability="write_members"><MemberEditPage /></SelfOrCapabilityRoute></ProtectedRoute>} />
      <Route path="/associados/:memberId/departamentos" element={<ProtectedRoute><CapabilityRoute capability="write_departments"><MemberDepartmentPage /></CapabilityRoute></ProtectedRoute>} />
      <Route path="/associados/:memberId/departamentos/:assignmentId" element={<ProtectedRoute><CapabilityRoute capability="write_departments"><MemberDepartmentPage /></CapabilityRoute></ProtectedRoute>} />
      <Route path="/associados/:memberId/senha" element={<ProtectedRoute><SupportCredentialsPage /></ProtectedRoute>} />
      <Route path="/credenciais/suporte" element={<ProtectedRoute><SupportCredentialsPage /></ProtectedRoute>} />
      <Route path="/acessos" element={<ProtectedRoute><AccessManagementPage /></ProtectedRoute>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function AppFrame() {
  const location = useLocation();
  const isLogin = location.pathname === "/login";

  return (
    <div className={`app-shell${isLogin ? " app-shell--login" : ""}`}>
      {!isLogin && <><a className="skip-link" href="#main-content">Saltar para o conteúdo principal</a><AppHeader /></>}
      <AppRoutes />
    </div>
  );
}

export default function App() {
  return (
    <CredentialRevealGuardProvider>
      <AuthProvider>
        <AppFrame />
      </AuthProvider>
    </CredentialRevealGuardProvider>
  );
}
